/**
 * <RoomOperationalCard> · ADR-FO-1 AR-2
 * ────────────────────────────────────────────────────────────────
 * Reusable everywhere a room is referenced operationally:
 *   Front Office · Housekeeping · Engineering · Reservations · Dashboard
 *
 * Renders: room # + type + snapshot status + HK status + engineering hold +
 * current guest + arrival / departure + occupancy + VIP + DND + OOS +
 * cleaning progress + quick-actions slot.
 *
 * Pure UI. All data from props.
 */
import React from "react";
import {
  BedDouble, Users2, CalendarCheck, CalendarX, AlertOctagon, Ban,
  Sparkles, Wrench, Crown,
} from "lucide-react";
import { StatusChip, resolveStatusKey, getStatusToken } from "./index";

// DoNotDisturb icon missing from lucide-react — use Ban with red tone as
// fallback. If you prefer a moon icon, swap here.
const _DoNotDisturbIcon = Ban;

export function RoomOperationalCard({
  room,          // {id, number, type_label?, floor?, snapshot_status,
                  //   hk_status?, em_status?, current_guest?, arrival?,
                  //   departure?, occupancy_pct?, vip?, dnd?, oos?,
                  //   cleaning_progress_pct?}
  size = "md",   // sm | md | lg — controls padding + font
  onClick,
  quickActions,  // ReactNode slot
  loading = false,
  disabled = false,
  testid,
}) {
  if (loading) {
    return (
      <div
        data-testid={testid ? `${testid}-loading` : "room-card-loading"}
        className="animate-pulse rounded-xl border-2 border-slate-200 bg-slate-50 min-h-[112px] p-4"
      />
    );
  }
  if (!room) {
    return (
      <div
        data-testid={testid ? `${testid}-empty` : "room-card-empty"}
        className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 min-h-[112px] p-4
                    text-[13px] text-slate-400 flex items-center justify-center"
      >No room</div>
    );
  }

  const statusKey = resolveStatusKey(room.snapshot_status);
  const tok = getStatusToken(statusKey);
  const paddingClass = size === "sm" ? "p-3" : size === "lg" ? "p-6" : "p-4";
  const numberSize   = size === "sm" ? "text-[18px]" : size === "lg" ? "text-[28px]" : "text-[22px]";
  const Component = onClick ? "button" : "div";

  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid || `room-card-${room.number}`}
      className={[
        "block w-full text-left rounded-xl border-2 transition-all",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300",
        paddingClass, tok.tone.bg, tok.tone.border,
        onClick ? `${tok.tone.hoverBorder} hover:shadow-md active:scale-[0.99] cursor-pointer` : "",
        disabled ? "opacity-50 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {/* Row 1: number + type + flags */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <BedDouble className={`w-4 h-4 ${tok.tone.text} shrink-0`}/>
          <div className="min-w-0">
            <div className={`font-display font-bold ${numberSize} leading-none ${tok.tone.text}`}>
              {room.number}
            </div>
            {room.type_label && (
              <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                {room.type_label}
                {room.floor ? ` · Fl ${room.floor}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {room.vip && (
            <StatusChip status="active" label="VIP" size="sm"
                          testid={testid ? `${testid}-vip` : undefined}/>
          )}
          {room.dnd && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-300 text-red-700 text-[10px] font-semibold"
                    data-testid={testid ? `${testid}-dnd` : undefined}>
              <_DoNotDisturbIcon className="w-3 h-3"/> DND
            </span>
          )}
          {room.oos && (
            <StatusChip status="critical" label="OOS" size="sm"
                          testid={testid ? `${testid}-oos` : undefined}/>
          )}
        </div>
      </div>

      {/* Row 2: primary status chip */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <StatusChip status={statusKey} label={tok.label} size="sm"
                       testid={testid ? `${testid}-status` : undefined}/>
        {room.hk_status && room.hk_status !== room.snapshot_status && (
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-600 bg-white/60 border border-slate-200 rounded px-1.5 py-0.5"
                  data-testid={testid ? `${testid}-hk-status` : undefined}>
            <Sparkles className="w-3 h-3"/> HK: {room.hk_status}
          </span>
        )}
        {room.em_status && (
          <span className="inline-flex items-center gap-1 text-[10px] text-orange-700 bg-orange-50 border border-orange-300 rounded px-1.5 py-0.5"
                  data-testid={testid ? `${testid}-em-status` : undefined}>
            <Wrench className="w-3 h-3"/> {room.em_status}
          </span>
        )}
      </div>

      {/* Row 3: current guest */}
      {room.current_guest && (
        <div className="flex items-center gap-1.5 text-[12px] text-slate-700 mb-1 truncate"
              data-testid={testid ? `${testid}-guest` : undefined}>
          <Users2 className="w-3 h-3 text-slate-400 shrink-0"/>
          <span className="font-medium truncate">{room.current_guest}</span>
          {room.vip && <Crown className="w-3 h-3 text-amber-500 shrink-0"/>}
        </div>
      )}

      {/* Row 4: arrival / departure */}
      {(room.arrival || room.departure) && (
        <div className="flex items-center gap-3 text-[11px] text-slate-600">
          {room.arrival && (
            <span className="inline-flex items-center gap-1">
              <CalendarCheck className="w-3 h-3 text-emerald-500"/>
              {room.arrival}
            </span>
          )}
          {room.departure && (
            <span className="inline-flex items-center gap-1">
              <CalendarX className="w-3 h-3 text-red-500"/>
              {room.departure}
            </span>
          )}
        </div>
      )}

      {/* Row 5: cleaning progress bar (when status=pending/cleaning) */}
      {typeof room.cleaning_progress_pct === "number" && (
        <div className="mt-2" data-testid={testid ? `${testid}-progress` : undefined}>
          <div className="h-1.5 rounded bg-slate-200 overflow-hidden">
            <div className="h-full bg-amber-500 transition-all"
                    style={{ width: `${Math.max(0, Math.min(100, room.cleaning_progress_pct))}%` }}/>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Cleaning · {room.cleaning_progress_pct}%
          </div>
        </div>
      )}

      {/* Row 6: occupancy pct (dashboard rooms overview) */}
      {typeof room.occupancy_pct === "number" && (
        <div className="mt-2 text-[11px] text-slate-500">
          Occupancy: <span className="font-medium text-slate-700">{room.occupancy_pct}%</span>
        </div>
      )}

      {/* Row 7: quick actions slot */}
      {quickActions && (
        <div className="mt-3 pt-2 border-t border-slate-200/60 flex flex-wrap gap-1"
               data-testid={testid ? `${testid}-actions` : undefined}
               onClick={(e) => e.stopPropagation()}>
          {quickActions}
        </div>
      )}
    </Component>
  );
}
